<template>
  <div class="border-top p-3">
    <div class="eyebrow-label mb-3">
      {{ $t("go_cancel.shared_defaults_title") }}
    </div>

    <div v-if="props.hasInconsistentSharedFields" class="advisory-note mb-3">
      <FontAwesomeIcon
        :icon="['far', 'circle-info']"
        class="flex-shrink-0 mt-1"
      />
      <div>
        {{ $t("go_cancel.analysis_shared_fields_inconsistent_warning") }}
      </div>
    </div>

    <div class="row g-3">
      <div class="col-md-2">
        <label class="form-label">
          {{ $t("go_cancel.form.beneficiary_type.label") }}
          <span class="text-danger">*</span>
        </label>
        <select
          v-model="sharedDefaults.beneficiary_type"
          class="form-select form-select-sm"
          @change="$emit('change')"
        >
          <option
            v-for="beneficiaryType in ALLOWED_CANCEL_BENEFICIARY_TYPES"
            :key="beneficiaryType"
            :value="beneficiaryType"
          >
            {{ $t(`go_cancel.form.beneficiary_type.${beneficiaryType}`) }}
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">
          {{ $t("go_cancel.form.period_start.label") }}
          <span class="text-danger">*</span>
        </label>
        <input
          v-model="sharedDefaults.period_start"
          type="date"
          class="form-control form-control-sm"
          :class="{
            'is-invalid':
              props.touched &&
              (!sharedDefaults.period_start || isPeriodStartAfterCutoff),
          }"
          @change="$emit('change')"
        />
        <div
          v-if="props.touched && isPeriodStartAfterCutoff"
          class="invalid-feedback"
        >
          {{
            $t("go_cancel.form.prior_year_cutoff_specific", {
              year: periodStartCutoffYear,
              cutoffDate: `31.03.${periodStartCutoffYear + 1}`,
            })
          }}
        </div>
      </div>
      <div class="col-md-3">
        <label class="form-label">
          {{ $t("go_cancel.form.period_end.label") }}
          <span class="text-danger">*</span>
        </label>
        <input
          v-model="sharedDefaults.period_end"
          type="date"
          class="form-control form-control-sm"
          :class="{
            'is-invalid':
              props.touched &&
              (isPeriodEndBeforeStart ||
                !sharedDefaults.period_end ||
                isPeriodEndAfterCutoff),
          }"
          :min="sharedDefaults.period_start || undefined"
          @change="$emit('change')"
        />
        <div
          v-if="
            props.touched && (isPeriodEndBeforeStart || isPeriodEndAfterCutoff)
          "
          class="invalid-feedback"
        >
          <span v-if="isPeriodEndBeforeStart">
            {{ $t("go_cancel.form.period_end.invalid_date_range") }}
          </span>
          <span v-else-if="isPeriodEndAfterCutoff">
            {{
              $t("go_cancel.form.prior_year_cutoff_specific", {
                year: cutoffYear,
                cutoffDate: `31.03.${cutoffNextYear}`,
              })
            }}
          </span>
        </div>
        <div class="form-text">
          {{
            $t("go_cancel.form.period_end.cutoff_help", {
              year: cutoffYear,
              nextYear: cutoffNextYear,
            })
          }}
        </div>
      </div>
      <div class="col-md-4">
        <label class="form-label">
          {{ $t("go_cancel.form.cancellation_purpose_text.label") }}
          <span class="text-danger">*</span>
        </label>
        <input
          v-model="sharedDefaults.cancellation_purpose_template"
          type="text"
          class="form-control form-control-sm"
          :class="{
            'is-invalid':
              props.touched &&
              !sanitizeOperationNotesTemplate(
                sharedDefaults.cancellation_purpose_template
              ),
          }"
          :placeholder="
            $t('go_cancel.form.cancellation_purpose_text.placeholder')
          "
          @input="$emit('change')"
        />
        <div class="form-text">
          {{ $t("go_cancel.form.cancellation_purpose_text.help") }}
        </div>
        <div
          v-if="props.hasAddressPlaceholderWithoutAddress"
          class="error-note mt-2 mb-0"
        >
          <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
          <span>{{ $t("go_cancel.address_placeholder_missing_warning") }}</span>
        </div>
      </div>
    </div>

    <BeneficiaryTypeConflictNotice
      v-if="props.beneficiaryTypeConflicts.length > 0"
      class="mt-3 mb-0"
      :conflicts="props.beneficiaryTypeConflicts"
      :sharedType="sharedDefaults.beneficiary_type"
      :switchableType="props.switchableBeneficiaryType"
    />

    <div v-if="showIncompleteWarning" class="error-note mt-3 mb-0">
      <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
      <span>{{ $t("go_cancel.shared_defaults_incomplete") }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import BeneficiaryTypeConflictNotice from "@/dashboard/inventory_management/cancel/BeneficiaryTypeConflictNotice.vue";
import type { BeneficiaryTypeConflict } from "@/dashboard/inventory_management/cancel/useCancelEditorAdvisories";
import {
  ALLOWED_CANCEL_BENEFICIARY_TYPES,
  type CancelSharedDefaults,
  isCancelSharedDefaultsValid,
  sanitizeOperationNotesTemplate,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import { isPriorYearPeriodAllowed } from "@/dashboard/inventory_management/common/cancellationPeriodRules";
import type { BeneficiaryTypeEnum } from "@/types";
import { computed } from "vue";

const props = defineProps<{
  touched: boolean;
  hasInconsistentSharedFields: boolean;
  hasAddressPlaceholderWithoutAddress: boolean;
  beneficiaryTypeConflicts: BeneficiaryTypeConflict[];
  switchableBeneficiaryType: BeneficiaryTypeEnum | null;
}>();

defineEmits<{
  change: [];
}>();

const sharedDefaults = defineModel<CancelSharedDefaults>("sharedDefaults", {
  required: true,
});

const isPeriodEndAfterCutoff = computed(() => {
  const { period_end } = sharedDefaults.value;
  if (!period_end) return false;
  return !isPriorYearPeriodAllowed(new Date(), period_end);
});

const isPeriodStartAfterCutoff = computed(() => {
  const { period_start } = sharedDefaults.value;
  if (!period_start) return false;
  return !isPriorYearPeriodAllowed(new Date(), period_start);
});

const isPeriodEndBeforeStart = computed(() => {
  const { period_start, period_end } = sharedDefaults.value;
  if (!period_start || !period_end) return false;
  return period_end < period_start;
});

const periodStartCutoffYear = computed(() =>
  yearFromDateString(sharedDefaults.value.period_start)
);

const cutoffYear = computed(() =>
  yearFromDateString(sharedDefaults.value.period_end)
);

const cutoffNextYear = computed(() => cutoffYear.value + 1);

const showIncompleteWarning = computed(
  () => props.touched && !isCancelSharedDefaultsValid(sharedDefaults.value)
);

const yearFromDateString = (value?: string): number =>
  value ? Number(value.slice(0, 4)) : new Date().getFullYear();
</script>
